import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@astro/auth'
import { useCartStore } from '@astro/store'
import { useWishlist } from '../../hooks/useWishlist.js'
import AccountDropdown from './AccountDropdown'
import CallDropdown from './CallDropdown'
import BrandLogo from './BrandLogo'
import ZodiacLogo from './ZodiacLogo'
import { FaRegUser } from "react-icons/fa6";
import { GrPhone } from "react-icons/gr";
import { appUrl } from '@astro/config';
import { MdArrowOutward, MdOutlineArrowOutward } from "react-icons/md";

const QUICK_LINKS = [
  { label: 'MAIN SITE', href: 'website', active: false },
  { label: 'JYOTISH', href: 'jyotish', active: false },
  { label: 'ACADEMY', href: 'education', active: false },
  { label: 'DIVINE SHOP', href: 'shop', active: true },
];

const NAV = [
  { label: 'HOME', to: '/', internal: true },
  { label: 'PRODUCTS', to: '/products', internal: true },
  { label: 'POOJA', to: '/pooja-energization', internal: true },
  { label: 'CONSULTATION', to: appUrl('jyotish'), internal: false },
  { label: 'ACADEMY', to: appUrl('education'), internal: false },
  { label: 'ASTRO TOOLS', to: appUrl('website', "astro-tools"), internal: false },
  { label: 'BLOGS', to: appUrl('website'), internal: false },
]

export default function Header() {
  const [profileOpen, setProfileOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const profileRef = useRef(null);
  const mobileProfileRef = useRef(null);
  const callRef = useRef(null);
  const { pathname } = useLocation();
  const { status, user, login, logout } = useAuth();
  const cartCount = useCartStore((s) => s.count());
  const authed = status === 'authenticated';

  // Wishlist is a signed-in-only feature — no local/guest state, badge count
  // comes straight from the server. Shares the same ['wishlist'] query (and
  // its cache) with every heart icon on the page via this one hook.
  const { count: wishlistCount } = useWishlist()
  const name = authed && user ? user.name || user.email : 'Account';
  // Chip label: the handle only — a truncated raw email ("DEBA@GMAIL…") reads badly.
  const displayName = name.split('@')[0];
  const initial = (name.trim()[0] || 'A').toUpperCase();

  // Transparent over dark heroes at the top of the page; solid white on scroll.
  // Only home, the products list and pooja-energization have a full dark hero
  // behind the bar; every other route is light-topped, so the bar stays solid.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const overlayRoute = pathname === '/' || pathname === '/products' || pathname === '/pooja-energization'
  const solid = scrolled || !overlayRoute

  useEffect(() => {
    if (!profileOpen) return undefined
    // Two anchors share this state — the sm+ chip and the mobile avatar — so a
    // click only closes the panel when it lands outside both.
    const onClick = (e) => {
      const inside = profileRef.current?.contains(e.target) || mobileProfileRef.current?.contains(e.target)
      if (!inside) setProfileOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [profileOpen])

  useEffect(() => {
    if (!callOpen) return undefined
    const onClick = (e) => {
      if (callRef.current && !callRef.current.contains(e.target)) setCallOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [callOpen])

  const isActive = (item) => item.internal && (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to));

  const NavLink = ({ item }) => {
    const active = isActive(item)
    const cls = `transition-colors flex items-center ${active
      ? `text-goldPrimary ${solid ? 'hover:text-primary' : 'hover:text-white'}`
      : solid
        ? 'text-primary hover:text-goldPrimary'
        : 'text-white/70 hover:text-goldPrimary'
      }`
    return item.internal ? (
      <Link to={item.to} className={cls}>{item.label}</Link>
    ) : (
      <a href={item.to} className={cls}>{item.label} <MdOutlineArrowOutward /> </a>
    )
  }

  // Display + padding are set per element (icons hide/shrink per breakpoint).
  const actionBtn = `rounded-full border border-transparent transition-all items-center justify-center ${solid ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:text-goldPrimary hover:bg-white/5 hover:border-white/10'}`

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-110">

        {/* Sticky top promo strip */}
        <div className="bg-linear-to-r from-[#1C120C] via-astroDark to-[#1C120C] border-b border-goldPrimary/10 py-2.5 px-4 sm:px-12 text-[8px] sm:text-[10px] tracking-[0.2em] font-semibold text-white/60 flex flex-col lg:flex-row justify-between items-center select-none gap-2.5 sm:gap-4">

          <div className="flex items-center gap-1 uppercase">
            <span className="text-goldPrimary animate-pulse">✦</span>Astro RAJU MAHARAJ JI{' '}
            <span className="text-goldPrimary animate-pulse">✦</span>
          </div>

          <nav className="hidden lg:flex items-center justify-center gap-4 sm:gap-6 uppercase overflow-x-auto max-w-full no-scrollbar pb-1 sm:pb-0">
            {QUICK_LINKS.map((link) => (
              <a
                key={link.label}
                href={appUrl(link.href)}
                className={`hover:text-goldPrimary  flex items-center ${link.active ? "bg-secondary/15 text-goldPrimary px-3 py-1 rounded" : ""}`}
              >
                {link.label}
                <span className={`ml-1 ${link.active ? "text-goldPrimary hidden" : ""}`}>
                  <MdArrowOutward size={14} />
                </span>
              </a>
            ))}
          </nav>
        </div>

        {/* Main nav row */}
        <div className="relative">
          {/* Transparent over the dark hero at the top; solid white with blur on scroll. */}
          <div className={`absolute inset-0 -z-10 border-b transition-all duration-300 ${solid ? 'backdrop-blur-md bg-white/95 border-black/10 shadow-sm' : 'bg-transparent border-transparent'}`}></div>
          <nav id="navbar" className="py-4 transition-all duration-300">
            {/* Wider cap from 2xl — at max-w-7xl the row's content (logo + 6 links
              + actions) overflows 1184px and squeezes the nav into wrapping. */}
            <div className="max-w-7xl 2xl:max-w-400 mx-auto px-4 sm:px-6 lg:px-12 flex items-center justify-between gap-4 relative">
              <a
                href={appUrl('website')}
                className="flex items-center gap-2 sm:gap-3 shrink-0">
                <BrandLogo className={`h-10 w-auto sm:h-12 max-w-20 rounded-xl transition-all duration-300 ${solid ? '' : 'bg-[#FBF3E4] p-1.5 border border-goldPrimary/40 shadow-lg shadow-black/30'}`}>
                  <ZodiacLogo className="w-9 h-9 sm:w-11 sm:h-11 shrink-0" />
                </BrandLogo>
                <div className="flex flex-col">
                  <span className={`font-royal text-sm sm:text-base font-bold tracking-widest leading-none transition-colors ${solid ? 'text-primary' : 'text-goldPrimary'}`}>
                    ASTRO RAJU MAHARAJ JI
                  </span>
                  <span className={`text-[8px] sm:text-[9px] font-semibold tracking-[0.25em] uppercase mt-1 transition-colors ${solid ? 'text-primary/50' : 'text-white/50'}`}>
                    Vedic Wisdom Master
                  </span>
                </div>
              </a>

              {/* Desktop nav — nowrap so multi-word labels ("ASTRO TOOLS") never
                break onto two lines when the row gets tight. */}
              <div className="hidden lg:flex items-center gap-5 xl:gap-6 text-[11px] uppercase font-medium tracking-widest whitespace-nowrap">
                {NAV.map((item) => <NavLink key={item.label} item={item} />)}
              </div>

              {/* Actions — progressively disclosed: wishlist always, profile from
                sm, search/call except lg (the nav needs that room), cart icon
                until xl where the View Cart CTA (with badge) replaces it. */}
              <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 relative shrink-0">
                {/* Profile */}
                <div className="relative hidden sm:block" ref={profileRef}>
                  {authed ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileOpen((o) => !o)
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-full border border-transparent transition-all ${solid ? 'text-primary hover:bg-black/5' : 'text-white/80 hover:text-goldPrimary hover:bg-white/5 hover:border-white/10'}`}
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden border border-primary/20 bg-primary text-white">
                          {user?.avatarUrl ? (
                            <img src={user.avatarUrl} alt={name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[11px] uppercase leading-none font-bold">{initial}</span>
                          )}
                        </div>
                        <span className="hidden 2xl:block text-[11px] font-normal uppercase tracking-wider max-w-[12ch] truncate">{displayName}</span>
                        <svg className={`w-3.5 h-3.5 transition-transform duration-300 ${profileOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                      </button>

                      <AccountDropdown open={profileOpen} onNavigate={() => setProfileOpen(false)} />
                    </>
                  ) : (
                    <button type="button" onClick={() => login()} className={`flex p-2.5 ${actionBtn}`} aria-label="Sign in">
                      <FaRegUser size={20} />
                    </button>
                  )}
                </div>

                <button type="button" className={`hidden sm:flex lg:hidden xl:flex p-2.5 ${actionBtn}`} aria-label="Search">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                </button>

                <div className="relative">
                  <Link to="/wishlist" className={`flex p-2 sm:p-2.5 ${actionBtn}`} aria-label="Wishlist">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z" /></svg>
                  </Link>
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary text-white text-[8px] font-normal rounded-full flex items-center justify-center shadow-sm">{wishlistCount}</span>
                  )}
                </div>

                <div className="relative xl:hidden">
                  <Link to="/checkout" className={`flex p-2 sm:p-2.5 ${actionBtn}`} aria-label="Cart">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
                  </Link>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-secondary text-white text-[8px] font-normal rounded-full flex items-center justify-center shadow-sm">{cartCount}</span>
                  )}
                </div>

                {/* Mobile profile — the chip above starts at sm, so without this a
                    phone gives no signal of whether you are signed in. Kept last
                    in the row: the panel is right-anchored and 16rem wide, so a
                    left-of-cart anchor would push it off the screen edge. */}
                <div className="relative sm:hidden" ref={mobileProfileRef}>
                  {authed ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileOpen((o) => !o)
                        }}
                        className={`flex items-center p-1 ${actionBtn}`}
                        aria-label="Account"
                      >
                        <div className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden border border-primary/20 bg-primary text-white">
                          {user?.avatarUrl ? (
                            <img src={user.avatarUrl} alt={name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[11px] uppercase leading-none font-bold">{initial}</span>
                          )}
                        </div>
                      </button>
                      <AccountDropdown open={profileOpen} onNavigate={() => setProfileOpen(false)} />
                    </>
                  ) : (
                    <button type="button" onClick={() => login()} className={`flex p-2 ${actionBtn}`} aria-label="Sign in">
                      <FaRegUser size={18} />
                    </button>
                  )}
                </div>

                <div className="relative hidden sm:block lg:hidden xl:block" ref={callRef}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCallOpen((o) => !o)
                    }}
                    className={`flex p-2.5 ${actionBtn}`}
                    aria-label="Support Hotline"
                  >
                    <GrPhone size={20} />
                  </button>
                  <CallDropdown open={callOpen} />
                </div>

                <Link
                  to="/checkout"
                  className="hidden xl:inline-flex items-center justify-center whitespace-nowrap shine-btn bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-xs tracking-widest uppercase px-5 py-3 rounded-full shadow-lg shadow-goldPrimary/10 transition-transform hover:-translate-y-0.5"
                >
                  View Cart
                  {cartCount > 0 && (
                    <span className="ml-2 w-5 h-5 rounded-full bg-astroDark text-goldPrimary text-[10px] font-bold flex items-center justify-center">{cartCount}</span>
                  )}
                </Link>
              </div>

              {/* Mobile hamburger */}
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className={`flex lg:hidden focus:outline-none p-2 transition-colors ${solid ? 'text-primary hover:text-goldPrimary' : 'text-white/85 hover:text-goldPrimary'}`}
                aria-label="Toggle Menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </nav>
        </div>

      </header>

      {/* Mobile drawer — a sibling of <header>, not a child: the fixed z-110
          header creates a stacking context that would cap the drawer below
          the floating FABs (z-index 999). */}
      <div className={`fixed inset-0 bg-black/80 z-1000 transition-opacity duration-300 lg:hidden ${drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setDrawerOpen(false)} />
      <div className={`fixed top-0 left-0 h-full w-[85%] max-w-sm bg-astroDeep/95 backdrop-blur-2xl border-r border-white/5 z-1001 shadow-2xl transition-transform duration-500 lg:hidden flex flex-col ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-2">
            <BrandLogo className="h-10 w-auto max-w-16 rounded-xl sm:h-12 bg-[#FBF3E4] p-1.5 border border-goldPrimary/40 shadow-lg shadow-black/30">
              <ZodiacLogo className="w-9 h-9 shrink-0" />
            </BrandLogo>
            <span className="font-royal text-sm font-bold tracking-wider text-primary">ASTRO RAJU MAHARAJ JI</span>
          </div>
          <button type="button" onClick={() => setDrawerOpen(false)} className="p-2 text-white/60 hover:text-goldPrimary rounded-full" aria-label="Close">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18 18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-4 px-4 flex flex-col gap-1 text-sm font-semibold tracking-wide">
          <Link to="/wishlist" onClick={() => setDrawerOpen(false)} className={`flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-white/5 ${pathname.startsWith('/wishlist') ? 'text-goldPrimary bg-white/5' : 'text-white/80 hover:text-goldPrimary'}`}>
            WISHLIST
            {wishlistCount > 0 && <span className="w-5 h-5 bg-secondary text-white text-[9px] rounded-full flex items-center justify-center">{wishlistCount}</span>}
          </Link>
          
          {/* Dashboard lives in the website app — cross-app link. Signed-in only. */}
          {authed && (
            <a href={appUrl('website', '/dashboard')} onClick={() => setDrawerOpen(false)} className="flex items-center justify-between p-3 rounded-lg transition-colors text-white/80 hover:text-goldPrimary hover:bg-white/5">DASHBOARD</a>
          )}

          {NAV.map((item) =>
            item.internal ? (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setDrawerOpen(false)}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-white/5 ${isActive(item) ? 'text-goldPrimary bg-white/5' : 'text-white/80 hover:text-goldPrimary'}`}
              >
                {item.label}
              </Link>
            ) : (
              /* Cross-app links leave this SPA — close the drawer on the way out
                 and carry the same outward arrow the desktop nav uses. */
              <a
                key={item.label}
                href={item.to}
                onClick={() => setDrawerOpen(false)}
                className="flex items-center justify-between p-3 rounded-lg transition-colors text-white/80 hover:text-goldPrimary hover:bg-white/5"
              >
                {item.label} <MdOutlineArrowOutward className="opacity-50" />
              </a>
            )
          )}

          {/* Session row — Logout when signed in, Login otherwise. */}
          <button
            type="button"
            onClick={() => { setDrawerOpen(false); authed ? logout() : login() }}
            className="flex items-center justify-between p-3 rounded-lg transition-colors text-white/80 hover:text-goldPrimary hover:bg-white/5 text-left"
          >
            {authed ? 'LOGOUT' : 'LOGIN'}
          </button>

        </div>
        <div className="p-4 mt-auto border-t border-white/5">
          <Link to="/checkout" onClick={() => setDrawerOpen(false)} className="inline-flex items-center justify-center whitespace-nowrap bg-linear-to-r from-goldAccent to-goldPrimary px-6 py-2 w-full h-12 rounded-xl text-astroDark text-xs font-bold tracking-widest uppercase shadow-lg">VIEW CART</Link>
        </div>
      </div>
    </>
  )
}
